import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import {
  useEquipMimic,
  useEquipPantsu,
  useEquipment,
  usePlayerPantsu,
  useUnequipPantsu,
  useWaifus,
} from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useModalPayload, useUIStore } from '@/store/uiStore';
import { hapticNotify } from '@/lib/telegram';
import { cn } from '@/lib/cn';
import type { Pantsu } from '@/types';

interface Payload {
  waifuId: number;
}

export function PantsuModal() {
  const open = useUIStore((s) => s.isModalOpen('pantsu'));
  const closeModal = useUIStore((s) => s.closeModal);
  const payload = useModalPayload<Payload>('pantsu');
  const userId = useAuthStore((s) => s.userId) ?? 0;
  const pushNotification = useUIStore((s) => s.pushNotification);

  const waifuId = payload?.waifuId ?? 0;
  const { data: ownedData } = usePlayerPantsu(open ? userId : null);
  const { data: equipmentData } = useEquipment(
    open ? waifuId : undefined,
    userId,
  );
  const { data: waifusData } = useWaifus(open ? userId : null);

  const equip = useEquipPantsu(waifuId, userId);
  const equipMimic = useEquipMimic(waifuId, userId);
  const unequip = useUnequipPantsu(waifuId, userId);

  const [mode, setMode] = useState<'own' | 'mimic'>('own');
  const [selectedMimicTarget, setSelectedMimicTarget] = useState<string | null>(
    null,
  );

  if (!open || !waifuId) return null;

  const owned = ownedData?.pantsu ?? [];
  const mimicTargets = owned.filter((p) => p.id !== 'mimic_panties');
  const hasMimicItem = owned.some((p) => p.id === 'mimic_panties');

  const equippedPantsu = (equipmentData?.equipment.pantsu ?? null) as
    | Pantsu
    | null;
  const currentWaifu = waifusData?.waifus.find((w) => w.id === waifuId);
  const isMimic =
    !!equippedPantsu &&
    currentWaifu?.original_pantsu_id === 'mimic_panties';

  const handleEquip = async (pantsu: Pantsu) => {
    if (pantsu.id === 'mimic_panties') {
      setMode('mimic');
      return;
    }
    try {
      await equip.mutateAsync({ pantsu_id: pantsu.id });
      hapticNotify('success');
      pushNotification({ type: 'success', message: 'Pantsu надеты' });
      closeModal('pantsu');
    } catch (err) {
      hapticNotify('error');
      const message = err instanceof Error ? err.message : 'Не удалось надеть';
      pushNotification({ type: 'error', message });
    }
  };

  const handleMimic = async () => {
    if (!selectedMimicTarget) return;
    try {
      await equipMimic.mutateAsync({ copy_target: selectedMimicTarget });
      hapticNotify('success');
      pushNotification({ type: 'success', message: 'Мимикрия активирована' });
      closeModal('pantsu');
    } catch (err) {
      hapticNotify('error');
      const message =
        err instanceof Error ? err.message : 'Не удалось скопировать';
      pushNotification({ type: 'error', message });
    }
  };

  const handleUnequip = async () => {
    try {
      await unequip.mutateAsync();
      hapticNotify('success');
      closeModal('pantsu');
    } catch {
      /* silent */
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => closeModal('pantsu')}
      title="Pantsu"
      size="lg"
      footer={
        <div className="flex gap-2">
          {equippedPantsu && (
            <Button variant="ghost" fullWidth onClick={handleUnequip}>
              Снять
            </Button>
          )}
          {mode === 'mimic' && (
            <Button
              fullWidth
              onClick={handleMimic}
              disabled={!selectedMimicTarget || !hasMimicItem || equipMimic.isPending}
            >
              {equipMimic.isPending ? 'Копируем…' : 'Подражать'}
            </Button>
          )}
        </div>
      }
    >
      <div className="p-4 space-y-3">
        <EquippedPantsuCard
          equipped={equippedPantsu}
          isMimic={isMimic}
        />

        <div className="flex gap-1 bg-bg-elevated p-1 rounded-xl">
          <button
            onClick={() => setMode('own')}
            className={cn(
              'flex-1 px-3 py-1.5 rounded-lg text-sm transition',
              mode === 'own' ? 'bg-accent text-white' : 'text-white/60',
            )}
          >
            Свои
          </button>
          <button
            onClick={() => setMode('mimic')}
            className={cn(
              'flex-1 px-3 py-1.5 rounded-lg text-sm transition',
              mode === 'mimic' ? 'bg-accent text-white' : 'text-white/60',
            )}
            disabled={!hasMimicItem}
          >
            Мимик{!hasMimicItem && ' (нет)'}
          </button>
        </div>

        {mode === 'own' ? (
          <div className="grid grid-cols-3 gap-2">
            {owned.length === 0 && (
              <div className="col-span-3 text-white/40 text-sm">
                Нет pantsu в инвентаре
              </div>
            )}
            {owned.map((p) => (
              <button
                key={p.id}
                onClick={() => handleEquip(p)}
                className="p-2 rounded-xl bg-bg-elevated border border-white/5 hover:border-white/20 active:scale-95 transition text-left"
              >
                <img
                  src={`/data/items/${p.icon}`}
                  alt={p.name}
                  className="w-full aspect-square object-contain mb-1"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div className="text-[11px] text-white font-medium truncate">
                  {p.name}
                </div>
                {p.quantity && p.quantity > 1 && (
                  <div className="text-[10px] text-white/50">×{p.quantity}</div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <>
            {!hasMimicItem && (
              <div className="text-white/50 text-sm p-3 rounded-xl bg-bg-elevated border border-white/5">
                Нет mimic_panties в инвентаре. Купи или получи в наградах.
              </div>
            )}
            {hasMimicItem && (
              <div className="grid grid-cols-3 gap-2">
                {mimicTargets.length === 0 && (
                  <div className="col-span-3 text-white/40 text-sm">
                    Нет pantsu, которые можно скопировать
                  </div>
                )}
                {mimicTargets.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedMimicTarget(p.id)}
                    className={cn(
                      'p-2 rounded-xl border transition text-left active:scale-95',
                      selectedMimicTarget === p.id
                        ? 'bg-accent/15 border-accent'
                        : 'bg-bg-elevated border-white/5 hover:border-white/20',
                    )}
                  >
                    <img
                      src={`/data/items/${p.icon}`}
                      alt={p.name}
                      className="w-full aspect-square object-contain mb-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="text-[11px] text-white truncate">
                      {p.name}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function EquippedPantsuCard({
  equipped,
  isMimic,
}: {
  equipped: Pantsu | null;
  isMimic: boolean;
}) {
  if (!equipped) {
    return (
      <div className="text-white/50 text-sm p-3 rounded-xl bg-bg-elevated border border-white/5">
        Pantsu не надеты
      </div>
    );
  }

  const hp = equipped.hp_bonus ?? 0;
  const str = equipped.str_bonus ?? 0;
  const dex = equipped.dex_bonus ?? 0;
  const intl = equipped.int_bonus ?? 0;

  const statsLine: string[] = [];
  if (hp > 0) statsLine.push(`HP +${hp}`);
  if (str > 0) statsLine.push(`STR +${str}`);
  if (dex > 0) statsLine.push(`DEX +${dex}`);
  if (intl > 0) statsLine.push(`INT +${intl}`);

  return (
    <div className="flex gap-3 p-3 rounded-xl bg-bg-elevated border border-white/10">
      <img
        src={`/data/items/${equipped.icon}`}
        alt={equipped.name}
        className="w-16 h-16 object-contain shrink-0"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
        draggable={false}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="text-sm font-medium text-white flex items-center gap-2 flex-wrap">
          <span className="truncate">{equipped.name}</span>
          {isMimic && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-300 shrink-0">
              Мимик
            </span>
          )}
        </div>
        {equipped.description && (
          <div className="text-[11px] text-white/65 leading-snug">
            {equipped.description}
          </div>
        )}
        {equipped.effect && (
          <div className="text-[11px] text-accent/90 leading-snug">
            {equipped.effect}
          </div>
        )}
        {statsLine.length > 0 && (
          <div className="text-[11px] text-white/70 tabular-nums">
            {statsLine.join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}
